import type { NetworkClientSignals, WebrtcCandidate } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync, withTimeout } from '../util/safe.js';

/**
 * Сетевые сигналы, доступные из браузера.
 *
 * Здесь только то, что видно клиенту: локальные ICE-кандидаты и оценка
 * соединения. Настоящая проверка утечек — сравнение адреса из WebRTC с
 * адресом TCP-соединения, определение DNS-резолвера, проба QUIC — требует
 * серверной стороны и появится на этапах 3–5. Пока фиксируем то, что можно
 * получить без своей инфраструктуры.
 *
 * Отдельно отмечаем полностью выключенный WebRTC: в обычном десктопном Chrome
 * он включён всегда, и его отсутствие означает расширение приватности или
 * настройку антидетект-браузера.
 */

const PRIVATE_V4 =
  /^(10\.|127\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.)/;
const PRIVATE_V6 = /^(::1$|fc|fd|fe80:)/i;

function parseCandidate(line: string): WebrtcCandidate | null {
  // candidate:<foundation> <component> <protocol> <priority> <address> <port> typ <type> ...
  const match = line.match(
    /candidate:\S+ (\d+) (\S+) (\d+) (\S+) (\d+) typ (\S+)/i,
  );
  if (!match) return null;

  const [, , protocol, , address, port, type] = match;
  const isMdns = /\.local$/i.test(address);

  return {
    type,
    protocol: protocol.toLowerCase(),
    address,
    port: Number(port),
    isMdns,
    isPrivate: isMdns || PRIVATE_V4.test(address) || PRIVATE_V6.test(address),
  };
}

async function gatherCandidates(): Promise<WebrtcCandidate[] | null> {
  return attemptAsync(async () => {
    const Ctor = (globalThis as { RTCPeerConnection?: typeof RTCPeerConnection })
      .RTCPeerConnection;
    if (!Ctor) return null;

    // Без ICE-серверов собираются только host-кандидаты: наружу ничего
    // не уходит, запрос к чужому STUN не отправляется.
    const pc = new Ctor({ iceServers: [] });

    try {
      pc.createDataChannel('fraudeai');
      const candidates: WebrtcCandidate[] = [];

      const gathering = new Promise<void>((resolve) => {
        pc.addEventListener('icecandidate', (event) => {
          if (!event.candidate || !event.candidate.candidate) return resolve();
          const parsed = parseCandidate(event.candidate.candidate);
          if (parsed) candidates.push(parsed);
        });
        pc.addEventListener('icegatheringstatechange', () => {
          if (pc.iceGatheringState === 'complete') resolve();
        });
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await withTimeout(gathering, 2500, 'ice').catch(() => undefined);

      return candidates;
    } finally {
      attempt(() => pc.close());
    }
  });
}

export const networkModule = defineModule({
  id: 'network',
  phase: 1,
  timeoutMs: 6000,
  async run(): Promise<NetworkClientSignals> {
    const webrtcSupported = 'RTCPeerConnection' in globalThis;
    const candidates = webrtcSupported ? await gatherCandidates() : null;

    const localAddresses = candidates
      ? [...new Set(candidates.filter((c) => c.isPrivate && !c.isMdns).map((c) => c.address ?? ''))]
          .filter(Boolean)
      : null;

    const connection = attempt(() => {
      const c = (navigator as Navigator & { connection?: Record<string, unknown> }).connection;
      if (!c) return null;
      return {
        effectiveType: typeof c['effectiveType'] === 'string' ? c['effectiveType'] : null,
        rtt: typeof c['rtt'] === 'number' ? c['rtt'] : null,
        downlink: typeof c['downlink'] === 'number' ? c['downlink'] : null,
        saveData: typeof c['saveData'] === 'boolean' ? c['saveData'] : null,
      };
    });

    return {
      webrtcSupported,
      candidates,
      localAddresses,
      mdnsObfuscated: candidates ? candidates.some((c) => c.isMdns) : null,
      webrtcDisabled: !webrtcSupported || (candidates !== null && candidates.length === 0),
      connection,
    };
  },
});
